import React, { useState, useEffect } from 'react';
import { analysisService } from '../../services/api';

interface AnalysisResult {
    score: number;
    status: string;
    isCompliant?: boolean;
    riskLevel?: string;
    breakdown?: {
        legal?: number;
        syntax?: number;
        completeness?: number;
    };
    issues?: Array<{
        id: string;
        type: string;
        severity: string;
        title?: string;
        description: string;
        recommendation?: string;
    }>;
    summary?: string;
    source?: string;
    model?: string;
    processingTimeMs?: number;
}

const NLPAnalyzer: React.FC = () => {
    const [text, setText] = useState('');
    const [result, setResult] = useState<AnalysisResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [analysisType, setAnalysisType] = useState<'pattern' | 'llm' | 'smart'>('smart');
    const [llmStatus, setLlmStatus] = useState<string | null>(null);

    // Initial check on mount
    useEffect(() => {
        console.log('NLPAnalyzer mounted');
        // Optional: Check connection immediately
        // checkLLMStatus(); 
    }, []);

    // Sample bad tenancy agreement for testing
    const sampleBadAgreement = `ASSURED SHORTHOLD TENANCY AGREEMENT
===================================

Fixed Term Assured Shorthold Tenancy

This Agreement is made on the 15th day of January 2026

BETWEEN:
Landlord: John Smith of 123 High Street, London
Tenant: Jane Doe of 456 Oak Avenue, Manchester

PROPERTY: 789 Elm Road, Birmingham, B1 1AA

1. TERM
This Fixed Term Assured Shorthold Tenancy shall be for a period of 12 months
commencing on 1st February 2026.

2. RENT
The Tenant agrees to pay rent of £1,200 per calendar month.

3. TERMINATION PROVISIONS
The Landlord may terminate this tenancy by serving:
- A Section 21 Notice giving two months' notice
- A Section 8 Notice for breach of tenancy conditions
- Written notice if rent is more than 14 days overdue

4. TENANT OBLIGATIONS
The Tenant agrees to:
- Keep the property clean and tidy
- Pay rent on time each month
- Not sublet any part of the property without written consent

5. DEPOSIT
A deposit of £1,200 is held. Note: if deposit not protected within 30 days
the tenant may claim compensation.`;

    const checkLLMStatus = async () => {
        setLlmStatus('Checking...');
        try {
            console.log('Calling analysisService.getStatus()...');
            const response = await analysisService.getStatus();
            console.log('Status response:', response);
            if (response.data?.data) {
                setLlmStatus(response.data.data.llmAvailable ? '✅ LLM Available' : '⚠️ Pattern Only');
            } else {
                setLlmStatus('❌ Unexpected Response');
            }
        } catch (err: any) {
            console.error('LLM Status Check Failed:', err);
            setLlmStatus('❌ Connection Failed');
        }
    };

    const handleAnalyze = async () => {
        if (!text.trim()) {
            setError('Please enter text to analyze');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            console.log(`Starting analysis: ${analysisType}`);
            let response;
            switch (analysisType) {
                case 'pattern':
                    response = await analysisService.scanText(text);
                    break;
                case 'llm':
                    response = await analysisService.analyzeLLM(text, false);
                    break;
                case 'smart':
                default:
                    response = await analysisService.analyzeSmartly(text);
                    break;
            }

            console.log('Analysis response:', response);

            if (response.data?.success) {
                setResult(response.data.data);
            } else {
                setError(response.data?.message || 'Analysis failed (No details)');
            }
        } catch (err: any) {
            console.error('Analysis Error:', err);
            setError(err.response?.data?.message || err.message || 'Analysis failed (Network/Server Error)');
        } finally {
            setLoading(false);
        }
    };

    const loadSampleText = () => {
        setText(sampleBadAgreement);
    };

    const getSeverityColor = (severity: string) => {
        switch (severity?.toUpperCase()) {
            case 'RED': return 'bg-red-100 text-red-800 border-red-300';
            case 'AMBER': return 'bg-amber-100 text-amber-800 border-amber-300';
            case 'GREEN': return 'bg-green-100 text-green-800 border-green-300';
            default: return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'PASS': return 'text-green-600';
            case 'WARN': return 'text-amber-600';
            case 'FAIL': return 'text-red-600';
            default: return 'text-gray-600';
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="bg-white rounded-2xl shadow-lg p-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Document Analyzer</h1>
                        <p className="text-gray-600 mt-1">
                            Test tenancy agreement analysis with AI-powered compliance checking
                        </p>
                    </div>
                    <button
                        onClick={checkLLMStatus}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                    >
                        {llmStatus || 'Check LLM Status'}
                    </button>
                </div>

                {/* Controls */}
                <div className="flex gap-4 mb-6">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Analysis Type
                        </label>
                        <select
                            value={analysisType}
                            onChange={(e) => setAnalysisType(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        >
                            <option value="smart">🧠 Smart (LLM + Fallback)</option>
                            <option value="llm">🤖 LLM Only (Claude)</option>
                            <option value="pattern">⚡ Pattern Only (Fast)</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={loadSampleText}
                            className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200"
                        >
                            Load Sample Bad Agreement
                        </button>
                    </div>
                </div>

                {/* Text Input */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tenancy Agreement Text
                    </label>
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Paste your tenancy agreement text here..."
                        className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                        {text.length} characters
                    </p>
                </div>

                {/* Analyze Button */}
                <button
                    onClick={handleAnalyze}
                    disabled={loading || !text.trim()}
                    className={`w-full py-4 rounded-lg font-semibold text-lg transition ${loading || !text.trim()
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                >
                    {loading ? '🔄 Analyzing...' : '🔍 Analyze Document'}
                </button>

                {/* Error */}
                {error && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                        ❌ {error}
                    </div>
                )}

                {/* Results */}
                {result && (
                    <div className="mt-8 space-y-6">
                        {/* Score Card */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-6 rounded-xl text-center">
                                <div className="text-5xl font-bold">{result.score}</div>
                                <div className="text-gray-400 mt-1">Score</div>
                            </div>
                            <div className={`p-6 rounded-xl text-center ${getStatusColor(result.status)} bg-gray-50`}>
                                <div className="text-3xl font-bold">{result.status}</div>
                                <div className="text-gray-500 mt-1">Status</div>
                            </div>
                            <div className="bg-gray-50 p-6 rounded-xl text-center">
                                <div className="text-2xl font-bold text-gray-800">{result.riskLevel || 'N/A'}</div>
                                <div className="text-gray-500 mt-1">Risk Level</div>
                            </div>
                            <div className="bg-gray-50 p-6 rounded-xl text-center">
                                <div className="text-lg font-medium text-gray-800">{result.source || 'pattern'}</div>
                                <div className="text-gray-500 mt-1">Analysis Source</div>
                                {result.processingTimeMs && (
                                    <div className="text-xs text-gray-400 mt-1">{result.processingTimeMs}ms</div>
                                )}
                            </div>
                        </div>

                        {/* Breakdown */}
                        {result.breakdown && (
                            <div className="bg-gray-50 p-6 rounded-xl">
                                <h3 className="font-semibold text-gray-800 mb-4">Score Breakdown</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <div className="text-sm text-gray-500">Legal</div>
                                        <div className="text-2xl font-bold">{result.breakdown.legal ?? 'N/A'}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500">Syntax</div>
                                        <div className="text-2xl font-bold">{result.breakdown.syntax ?? 'N/A'}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500">Completeness</div>
                                        <div className="text-2xl font-bold">{result.breakdown.completeness ?? 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Summary */}
                        {result.summary && (
                            <div className="bg-blue-50 p-6 rounded-xl">
                                <h3 className="font-semibold text-blue-800 mb-2">Summary</h3>
                                <p className="text-blue-700">{result.summary}</p>
                            </div>
                        )}

                        {/* Issues */}
                        {result.issues && result.issues.length > 0 && (
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-4">
                                    Issues Found ({result.issues.length})
                                </h3>
                                <div className="space-y-3">
                                    {result.issues.map((issue, index) => (
                                        <div
                                            key={index}
                                            className={`p-4 border rounded-lg ${getSeverityColor(issue.severity)}`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="font-semibold">
                                                    {issue.title || issue.id}
                                                </div>
                                                <span className="px-2 py-1 text-xs font-medium rounded">
                                                    {issue.severity}
                                                </span>
                                            </div>
                                            <p className="mt-2 text-sm">{issue.description}</p>
                                            {issue.recommendation && (
                                                <p className="mt-2 text-sm italic">
                                                    💡 {issue.recommendation}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NLPAnalyzer;
